// UrlsTable component for managing experience URLs
import { FormikProps } from "formik";
import { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FreelancerProfileProfessionalExperienceUrl } from "../../../../../models/FreelancerProfileProfessionalExperienceUrl";
import {
	FreelancerOnboardingMyPresentationExperienceViewModel,
	FreelancerOnboardingMyPresentationStepViewModel
} from "../../../../../ViewModels/FreelancerOnboardingMyPresentationStepViewModel";
import { normalizeUrl } from "../../FreelancerOnboardingMyPresentation";
import UrlTable from "../common/UrlTable";
import { Typography } from "@mui/material";

interface UrlsTableProps {
	readonly experienceIndex: number;
	readonly experience: FreelancerOnboardingMyPresentationExperienceViewModel;
	readonly formikRef: React.MutableRefObject<
		FormikProps<FreelancerOnboardingMyPresentationStepViewModel>
	>;
	readonly hasFieldError: (fieldPath: string) => boolean;
	readonly getFieldError: (fieldPath: string) => string | undefined;
	readonly isAddingUrl: boolean;
	readonly setIsAddingUrl: Dispatch<SetStateAction<boolean>>;
	readonly addingUrl: FreelancerProfileProfessionalExperienceUrl | null;
	readonly setAddingUrl: Dispatch<
		SetStateAction<FreelancerProfileProfessionalExperienceUrl | null>
	>;
	readonly showingUrlTable: boolean;
	readonly setErrorDialog: Dispatch<
		SetStateAction<{
			open: boolean;
			title: string;
			message: string;
		}>
	>;
}

export default function UrlsTable({
	experienceIndex,
	experience,
	formikRef,
	hasFieldError,
	getFieldError,
	isAddingUrl,
	setIsAddingUrl,
	addingUrl,
	setAddingUrl,
	showingUrlTable,
	setErrorDialog
}: UrlsTableProps) {
	const { t } = useTranslation();

	// Reset internal state when table becomes inactive
	useEffect(() => {
		if (!showingUrlTable) {
			setIsAddingUrl(false);
			setAddingUrl(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showingUrlTable]);

	// Function to show error dialog
	const showErrorDialog = useCallback((title: string, message: string) => {
		setErrorDialog({
			open: true,
			title,
			message
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleAddNewUrl = (experienceIndex: number) => {
		// Check URL count limit (5 URLs max)
		const currentUrls =
			formikRef.current.values.professionalExperiences[experienceIndex]?.urls || [];
		if (currentUrls.length >= 5) {
			showErrorDialog(
				t("freelancerOnboardingMyPresentation.validation.urlsMaxCountTitle"),
				t("freelancerOnboardingMyPresentation.validation.urlsMaxCount")
			);
			return;
		}

		setIsAddingUrl(true);
		setAddingUrl({
			url: "",
			freelancerProfileProfessionalExperienceId: 0
		} as FreelancerProfileProfessionalExperienceUrl);
	};

	const handleConfirmAddUrl = (experienceIndex: number) => {
		if (addingUrl?.url) {
			// Normalize URL by adding protocol if missing
			let normalizedUrl = addingUrl.url.trim();

			// Add https:// if URL starts with www. or doesn't have a protocol
			if (normalizedUrl.startsWith("www.") || !/^https?:\/\//i.test(normalizedUrl)) {
				normalizedUrl = `https://${normalizedUrl}`;
			}

			// Validate URL format and domain
			try {
				const urlObj = new URL(normalizedUrl);

				// Check if the hostname is valid
				const hostname = urlObj.hostname;

				// Basic validation: must contain at least one dot and a valid TLD
				const domainRegex =
					/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

				// Allow localhost for development
				if (!domainRegex.test(hostname)) {
					throw new Error("Invalid domain");
				}

				// Reject obvious test/placeholder domains
				const invalidDomains = ["test", "example", "placeholder", "demo", "temp"];
				if (invalidDomains.includes(hostname.toLowerCase())) {
					throw new Error("Invalid domain");
				}
			} catch {
				showErrorDialog(
					t("freelancerOnboardingMyPresentation.validation.invalidUrlFormatTitle"),
					t("freelancerOnboardingMyPresentation.validation.invalidUrlFormat")
				);
				return;
			}

			const currentExperience =
				formikRef.current.values.professionalExperiences[experienceIndex];

			// Check for duplicate URLs (compare normalized URLs)
			const normalizedUrls = [...currentExperience.urls, addingUrl]
				.map((urlItem: any) => {
					if (!urlItem?.url) return "";

					return normalizeUrl(urlItem.url).replace("www.", "").toLowerCase();
				})
				.filter(Boolean);

			const uniqueUrls = new Set(normalizedUrls);
			const isDuplicateUrl = normalizedUrls.length !== uniqueUrls.size;

			if (isDuplicateUrl) {
				showErrorDialog(
					t("freelancerOnboardingMyPresentation.validation.urlDuplicateTitle"),
					t("freelancerOnboardingMyPresentation.validation.urlDuplicate")
				);
				return;
			}

			// Update the URL with the normalized version
			const finalUrl = { ...addingUrl, url: normalizedUrl };
			const updatedUrls = [...(currentExperience.urls ?? []), finalUrl];
			formikRef.current.setFieldValue(
				`professionalExperiences.${experienceIndex}.urls`,
				updatedUrls
			);

			// Reset adding state but keep table visible
			setIsAddingUrl(false);
			setAddingUrl(null);
		}
	};

	const wrappedHandleAddNewUrl = () => handleAddNewUrl(experienceIndex);
	const wrappedHandleConfirmAddUrl = () => handleConfirmAddUrl(experienceIndex);

	const handleCancelAddUrl = useCallback(() => {
		setAddingUrl(null);
		setIsAddingUrl(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Function for removing URLs from the urls array
	const removeUrl = useCallback(
		(urlIndex: number) => {
			const currentExperience =
				formikRef.current.values.professionalExperiences[experienceIndex];
			const updatedUrls = currentExperience.urls.filter((_, i: number) => i !== urlIndex);
			formikRef.current.setFieldValue(
				`professionalExperiences.${experienceIndex}.urls`,
				updatedUrls
			);
		},
		[experienceIndex, formikRef]
	);

	return (
		<>
			<UrlTable
				urls={experience.urls || []}
				isVisible={showingUrlTable}
				isAddingUrl={isAddingUrl}
				addingUrl={addingUrl}
				onAddNewUrl={wrappedHandleAddNewUrl}
				onConfirmAddUrl={wrappedHandleConfirmAddUrl}
				onCancelAddUrl={handleCancelAddUrl}
				onRemoveUrl={removeUrl}
				onUrlChange={(url: any) => setAddingUrl(prev => ({ ...prev!, ...url }))}
				showTypeColumn={false}
				urlTypes={[]}
				tableWidth="100%"
			/>
			{/* URLs validation error */}
			{hasFieldError(`professionalExperiences.${experienceIndex}.urls`) && (
				<Typography color="error" variant="body2" sx={{ mt: 1, display: "block" }}>
					{getFieldError(`professionalExperiences.${experienceIndex}.urls`)}
				</Typography>
			)}
		</>
	);
}
